import { cn } from "@/nucleo/utilitarios";

interface PortalHeroProps {
  image: string;
  title: string;
  description?: string;
  children?: React.ReactNode;
  compact?: boolean;
}

/**
 * Cabeçalho reaproveitável das páginas internas do portal público do TEG.
 *
 * Mostra uma fotografia de fundo sob o véu institucional, com título e
 * descrição por cima.
 *
 * O VÉU É `--hero-overlay`, E NÃO `--primary`. O véu é translúcido — no ponto
 * mais fraco do degradê 30% da foto atravessa —, então o contraste do texto não
 * se mede contra a cor pura: mede-se contra a mistura. `--primary` é cor de
 * preenchimento de botão, e no tema escuro (213 70% 48%) essa mistura derrubava
 * o texto para 2,84:1 sobre foto clara, abaixo até do mínimo de 3:1 de texto
 * grande. `--hero-overlay` é escuro nos dois temas, de propósito, como
 * `--overlay`: o pior caso passa a 4,87:1. Ver o comentário do token em
 * `ui/estilos/tokens.css`.
 *
 * Pela mesma razão a descrição vai em opacidade cheia. Ela já foi
 * `text-primary-foreground/85`, e os 15% de véu que atravessavam o texto
 * custavam quase um ponto de contraste — o suficiente para reprovar.
 * `auditar-contraste.ts` confere os dois pontos do degradê a cada build.
 *
 * A PR #121 atacou o mesmo defeito por outro caminho: `primary` no tema claro
 * e `sidebar` no escuro, onde o texto voltava a 18:1. O token substitui aquela
 * solução e resolve o caso que ela não cobria — o tema de alto contraste, cujo
 * véu é amarelo e cujo texto é preto, e a medição contra a mistura com a foto,
 * que agora é conferida no build em vez de calculada à mão.
 */
export function PortalHero({ image, title, description, children, compact }: PortalHeroProps) {
  return (
    <section className="relative overflow-hidden" aria-labelledby="page-hero-title">
      <div className="absolute inset-0">
        <img
          src={image}
          alt=""
          role="presentation"
          className="h-full w-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-linear-to-r from-hero-overlay/95 via-hero-overlay/85 to-hero-overlay/70" />
      </div>
      <div className={cn(
        "relative mx-auto max-w-7xl px-4 sm:px-6",
        compact ? "py-12 sm:py-16" : "py-16 sm:py-20"
      )}>
        <div className="max-w-3xl animate-fade-in">
          <h1 id="page-hero-title" className="text-2xl sm:text-3xl sm:leading-9 lg:text-4xl lg:leading-10 font-bold tracking-tight text-hero-overlay-foreground leading-tight">
            {title}
          </h1>
          {description && (
            <p className="mt-3 text-base sm:text-lg sm:leading-7 text-hero-overlay-foreground max-w-2xl leading-relaxed">
              {description}
            </p>
          )}
          {children}
        </div>
      </div>
    </section>
  );
}
